/**
 * Form shapes for the auth pages.
 *
 * These mirror the server's rules rather than replacing them: the server is the authority, and
 * anything it rejects is shown next to the field it belongs to. Validating here only saves a
 * round trip on obvious mistakes.
 *
 * Messages are i18next keys, not English, so `t(issue.message)` renders them.
 */

import { z } from "zod";

/** Django's own minimum. Keeping the two in step means a password accepted here is accepted there. */
export const MIN_PASSWORD_LENGTH = 8;

const email = z
  .string()
  .trim()
  .min(1, "auth.errors.emailRequired")
  .email("auth.errors.emailInvalid");

const password = z.string().min(MIN_PASSWORD_LENGTH, "auth.errors.passwordTooShort");

export const signInSchema = z.object({
  email,
  // Not length-checked: an existing password that predates a rule change must still be typable.
  password: z.string().min(1, "auth.errors.passwordRequired"),
});

export const signUpSchema = z.object({
  email,
  password,
});

export const forgotPasswordSchema = z.object({ email });

export const resetPasswordSchema = z
  .object({
    password,
    passwordConfirm: z.string().min(1, "auth.errors.passwordRequired"),
  })
  .refine((values) => values.password === values.passwordConfirm, {
    message: "auth.errors.passwordMismatch",
    path: ["passwordConfirm"],
  });

export type SignInValues = z.infer<typeof signInSchema>;
export type SignUpValues = z.infer<typeof signUpSchema>;
export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;
